/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './device-dashboard-widget.scss';

/* eslint-disable import/no-unresolved, import/default, import/namespace */

import deviceDashboardWidgetTemplate from './device-dashboard-widget.tpl.html';
import spearSensorSvg from '../../svg/spear_sensor.svg';
import farmerMoistureSvg from '../../svg/farmer_moisture.svg';
import sensorsSpearImg from './../../img/cellular-spear.png';
import cProImg from './../../img/c-pro.jpg';
import superProImg from './../../img/Superpro.jpg';
import farmProImg from './../../img/Farmpro.jpg';
import superPointImg from './../../img/Superpoint.jpg';
import farmPointImg from './../../img/Farmpoint.jpg';


/* eslint-enable import/no-unresolved, import/default, import/namespace */

export default angular.module('agrolog.farmer.deviceDashboardWidget', [])
    .directive('deviceDashboardWidget', DeviceDashboardWidget)
    .name;

/*@ngInject*/
function DeviceDashboardWidget() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            device: '='
        },
        controller: DeviceDashboardWidgetController,
        controllerAs: 'vm',
        templateUrl: deviceDashboardWidgetTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function DeviceDashboardWidgetController($scope, $log, types, agUtils, agTypes, unitService) {

    let vm = this;

    vm.agTypes = agTypes;
    vm.agUtils = agUtils;

    vm.loading = false;
    vm.tempHistoryUnits = unitService.temperatureUnitSymbol();

    vm.getDeviceIcon = getDeviceIcon;
    vm.getDevicePic = getDevicePic;

    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: (subscription) => {
                vm.loading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        },
        smoothLines: true
    };

    vm.flotKeySettings = {
        lineWidth: 1
    };
    vm.legendConfig = {};

    vm.cropNames = [];
    for (let property in agTypes.farmerCropNames) {
        vm.cropNames.push({
            nameId: property,
            value: agTypes.farmerCropNames[property]
        });
    }

    $scope.$watch('vm.device', () => {
        reset();
        if (vm.device) {
            createLatestValuesSubscription();
            updateChartSubscription();
        }
    });

    function createLatestValuesSubscription() {
        let latestValuesSubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.device,
            entityId: vm.device.id.id,
            entityName: vm.device.name
        }];
        if (vm.device.type === vm.agTypes.farmerDeviceTypes.cellularSensorSpear.value) {
            latestValuesSubscriptionsInfo[0].timeseries = [
                {name: vm.agTypes.telemetry.spearSensor.temperature},
                {name: vm.agTypes.telemetry.spearSensor.ambientTemperature}
            ];
        } else if (agUtils.ifMoistureDevice(vm.device.type)) {
            latestValuesSubscriptionsInfo[0].timeseries = [{name: "measurement"}];
        }
        vm.ctx.subscriptionApi.createSubscriptionFromInfo (
            types.widgetType.latest.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
            (subscription) => {
                vm.latestValuesSubscription = subscription;
            }
        );
    }

    function updateChartSubscription() {
        vm.chartSubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.device,
            entityId: vm.device.id.id,
            entityName: vm.device.name,
            timeseries: [
                {
                    name: vm.agTypes.telemetry.spearSensor.temperature,
                    label: "Temp.",
                    color: agTypes.colors.maxColor,
                    postFuncBody: unitService.temperaturePostprocessingFuncBody()
                },
                {
                    name: vm.agTypes.telemetry.spearSensor.ambientTemperature,
                    label: "Ambient Temp.",
                    color: agTypes.colors.minColor,
                    postFuncBody: unitService.temperaturePostprocessingFuncBody()
                }
            ]
        }];
    }

    function onDataUpdated(subscription, apply) {
        let data = subscription.data;
        if (vm.device.type === vm.agTypes.farmerDeviceTypes.cellularSensorSpear.value) {
            for (let i = 0; i < data.length; i++) {
                let datasourceData = data[i];
                let val = getNumberValue(datasourceData);
                switch (datasourceData.dataKey.name) {
                    case vm.agTypes.telemetry.spearSensor.temperature:
                        vm.sampleTemp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 1, '')+'°';
                        break;
                    case vm.agTypes.telemetry.spearSensor.ambientTemperature:
                        vm.ambientTemp = vm.ctx.utils.formatValue(unitService.toUserTemperature(val), 1, '')+'°';
                        break;
                }
            }
        } else if (agUtils.ifMoistureDevice(vm.device.type)) {
            if (data[0].data.length) {
                let measurement = angular.fromJson(data[0].data[0][1]);
                vm.moistureValue = measurement.moistureContent;
                vm.moistureCrop = getCropName(measurement.cropType);
            }
        }
        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    $scope.$on('$destroy', () => {
        reset();
    });

    function getCropName(cropNameId) {
        if (vm.cropNames && vm.cropNames.length) {
            for (let i = 0; i < vm.cropNames.length; i++) {
                if (vm.cropNames[i].nameId === cropNameId) {
                    return vm.cropNames[i].value;
                }
            }
        }
        return "-";
    }

    function reset() {
        if (vm.latestValuesSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.latestValuesSubscription.id);
        }
        resetValues();
    }

    function resetValues() {
        vm.ambientTemp = '';
        vm.sampleTemp = '';
        vm.moistureValue = '-';
        vm.moistureCrop = '-';
    }

    function getDeviceIcon() {
        if (vm.device.type === vm.agTypes.farmerDeviceTypes.cellularSensorSpear.value) {
            return spearSensorSvg;
        } else if (agUtils.ifMoistureDevice(vm.device.type)) {
            return farmerMoistureSvg;
        }
        return "";
    }

    function getDevicePic() {
        switch (vm.device.type) {
            case agTypes.farmerDeviceTypes.cellularSensorSpear.value:
                return sensorsSpearImg;
            case agTypes.farmerDeviceTypes.cPro.value:
                return cProImg;
            case agTypes.farmerDeviceTypes.superPro.value:
                return superProImg;
            case agTypes.farmerDeviceTypes.farmPro.value:
                return farmProImg;
            case agTypes.farmerDeviceTypes.superPoint.value:
                return superPointImg;
            case agTypes.farmerDeviceTypes.farmPoint.value:
                return farmPointImg;
        }
        return null;
    }

    function getNumberValue(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1] && keyData.data[0][1] !== null)) {
            if (!isNaN(keyData.data[0][1])) {
                return Number(keyData.data[0][1]);
            } else {
                return "-";
            }
        } else {
            return "-";
        }
    }
}